import { Text, Group, Anchor } from '@mantine/core';
import { InfoIcon } from 'components/InfoIcon';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { TableConfig } from 'models/general';
import { BsSortDown, BsSortUp } from 'react-icons/bs';
import { Column } from 'utils/models';

interface Props<T> {
  data: Column<T>;
  config: TableConfig;
}

const renderHeader = (header: string, headerTooltip?: string) => {
  if (headerTooltip)
    return (
      <Group gap={'xs'} wrap={'nowrap'}>
        <Text c={'dimmed'}>{header}</Text>
        <InfoIcon i18nKey={headerTooltip} w={400} />
      </Group>
    );

  return <Text c={'dimmed'}>{header}</Text>;
};

export const TableHeaderItem = <T,>({ data, config }: Props<T>) => {
  const { queryKey, defaultSort } = config;
  const { parameters: filters, setParameters } = useSearchParameters(queryKey);

  if (data?.sortFieldName) {
    const sortValue = filters.sort || defaultSort || '';
    const isDescending = sortValue.startsWith('-');
    const sortedFieldName = isDescending ? sortValue.substring(1) : sortValue;
    const sorted = data.sortFieldName === sortedFieldName;

    const handleFilterChange = () => {
      if (sorted) {
        const sort = isDescending ? sortValue.substring(1) : `-${sortValue}`;
        setParameters({ ...filters, sort });
      } else {
        setParameters({ ...filters, sort: `-${data.sortFieldName}` || '' });
      }
    };

    return (
      <td width={data.width && `${data.width}%`}>
        <Anchor underline={'never'} onClick={() => handleFilterChange()} c={'dimmed'} fw={sorted ? 800 : 400}>
          <Group gap={'xs'} wrap={'nowrap'}>
            {data.header}
            <div>
              {sorted && isDescending && <BsSortDown />}
              {sorted && !isDescending && <BsSortUp />}
              {!sorted && <BsSortDown />}
            </div>
          </Group>
        </Anchor>
      </td>
    );
  }

  return <td width={data.width && `${data.width}%`}>{renderHeader(data.header, data.headerTooltip)}</td>;
};
